import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AvailabilityGrid } from "@/components/AvailabilityGrid";
import {
  Loader2, Check, X, Phone, CalendarClock, Users, Bell, Shield, Pencil, MessageSquare, Download, Send, Briefcase, Handshake,
} from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    requested: "bg-amber-100 text-amber-700",
    confirmed: "bg-green-100 text-green-700",
    declined: "bg-red-100 text-red-700",
    pending: "bg-amber-100 text-amber-700",
    active: "bg-green-100 text-green-700",
    sent: "bg-green-100 text-green-700",
    queued: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };
  return <span className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${map[status] || "bg-secondary text-muted-foreground"}`}>{status}</span>;
};

const Admin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [experts, setExperts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [hiring, setHiring] = useState([]);
  const [consulting, setConsulting] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) navigate("/");
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    try {
      const [s, b, e, n, c, h, cr, rec] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/bookings"),
        api.get("/admin/experts"), api.get("/admin/notifications"),
        api.get("/admin/candidates"), api.get("/admin/hiring-requests"),
        api.get("/admin/consultant-requests"), api.get("/admin/recruiters"),
      ]);
      setStats(s.data); setBookings(b.data); setExperts(e.data); setNotifications(n.data);
      setCandidates(c.data); setHiring(h.data); setConsulting(cr.data); setRecruiters(rec.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast.success(status === "confirmed" ? "Confirmed — customer notified on WhatsApp" : `Booking ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const runReminders = async () => {
    try {
      const res = await api.post("/admin/run-reminders");
      toast.success(`Reminder check complete — ${res.data.reminders_sent} sent`);
      load();
    } catch (e) {
      toast.error("Could not run reminders");
    }
  };

  const togglePublish = async (id, published) => {
    try {
      await api.put(`/admin/hiring-requests/${id}/publish`, { published });
      toast.success(published ? "Published to public job board" : "Unpublished");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const setRecruiterStatus = async (id, status) => {
    try {
      await api.put(`/admin/recruiters/${id}/status`, { status });
      toast.success(status === "approved" ? "Recruiter approved — they can now post" : `Recruiter ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (authLoading || loading || !user?.is_admin) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const statCards = [
    { label: "Experts", value: stats?.experts, icon: Users },
    { label: "Bookings", value: stats?.bookings, icon: CalendarClock },
    { label: "Candidates", value: stats?.candidates, icon: Briefcase },
    { label: "Hiring reqs", value: stats?.hiring_requests, icon: Users },
    { label: "Consulting", value: stats?.consultant_requests, icon: Handshake },
    { label: "Pending bookings", value: stats?.pending_bookings, icon: Bell },
  ];

  return (
    <div className="max-w-7xl mx-auto px-5 py-10 font-body" data-testid="admin-page">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="h-6 w-6 text-primary" />
        <h1 className="font-heading font-bold text-3xl tracking-tight">Admin Dashboard</h1>
      </div>
      <p className="text-muted-foreground mb-8">Manage bookings, expert contact details and notifications.</p>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((s) => (
          <Card key={s.label} className="p-5" data-testid={`stat-${s.label}`}>
            <s.icon className="h-5 w-5 text-primary mb-2" />
            <p className="text-2xl font-heading font-bold">{s.value ?? 0}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="bookings">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings & Leads</TabsTrigger>
          <TabsTrigger value="candidates" data-testid="tab-candidates">Candidates</TabsTrigger>
          <TabsTrigger value="hiring" data-testid="tab-hiring">Hiring Requests</TabsTrigger>
          <TabsTrigger value="recruiters" data-testid="tab-recruiters">Recruiters</TabsTrigger>
          <TabsTrigger value="consulting" data-testid="tab-consulting">Consulting</TabsTrigger>
          <TabsTrigger value="experts" data-testid="tab-experts">Experts & Phone</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* Bookings */}
        <TabsContent value="bookings" className="mt-5">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Expert</TableHead>
                  <TableHead>Slot</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No bookings yet.</TableCell></TableRow>
                ) : bookings.map((b) => (
                  <TableRow key={b.id} data-testid={`admin-booking-${b.id}`}>
                    <TableCell>
                      <p className="font-medium">{b.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{b.customer_designation || "—"}{b.customer_company ? ` · ${b.customer_company}` : ""}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm flex items-center gap-1"><Phone className="h-3 w-3" /> {b.customer_phone}</p>
                      <p className="text-xs text-muted-foreground">{b.customer_email}</p>
                    </TableCell>
                    <TableCell className="text-sm">{b.expert_name}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{b.date}<br /><span className="text-xs text-muted-foreground">{b.time_slot}</span></TableCell>
                    <TableCell><StatusBadge status={b.status} /></TableCell>
                    <TableCell className="text-right">
                      {b.status !== "confirmed" && (
                        <Button size="sm" className="rounded-full mr-1.5 h-8" onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Confirm
                        </Button>
                      )}
                      {b.status !== "declined" && (
                        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setStatus(b.id, "declined")} data-testid={`decline-${b.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Candidates */}
        <TabsContent value="candidates" className="mt-5">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Desired role</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Resume</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No candidates yet.</TableCell></TableRow>
                ) : candidates.map((c) => (
                  <TableRow key={c.id} data-testid={`admin-candidate-${c.id}`}>
                    <TableCell>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.industry || "—"} · {c.experience_years || 0} yrs · {c.location || "—"}</p>
                    </TableCell>
                    <TableCell className="text-sm">{c.desired_profile}</TableCell>
                    <TableCell>
                      <p className="text-xs">{c.email}</p>
                      <p className="text-xs text-muted-foreground">{c.phone || "—"}</p>
                    </TableCell>
                    <TableCell>
                      {c.resume_url ? (
                        <a href={c.resume_url} target="_blank" rel="noreferrer" className="text-primary underline text-xs inline-flex items-center gap-1" data-testid={`resume-link-${c.id}`}>
                          <Download className="h-3.5 w-3.5" /> View
                        </a>
                      ) : <span className="text-xs text-muted-foreground">None</span>}
                    </TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className="text-right">
                      <ReferDialog candidate={c} onDone={load} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Hiring requests */}
        <TabsContent value="hiring" className="mt-5">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Consult?</TableHead>
                  <TableHead>Public</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hiring.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No hiring requests yet.</TableCell></TableRow>
                ) : hiring.map((h) => (
                  <TableRow key={h.id} data-testid={`admin-hiring-${h.id}`}>
                    <TableCell className="font-medium">{h.company_name}<p className="text-xs text-muted-foreground font-normal">{h.industry || "—"}</p></TableCell>
                    <TableCell className="text-xs">{h.contact_name}<br />{h.contact_email}<br />{h.contact_phone || ""}</TableCell>
                    <TableCell className="text-sm">
                      {h.role_needed}
                      {h.profile_name ? <span className="text-muted-foreground"> · {h.profile_name}</span> : null}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[h.employment_type, h.location, h.experience_required, h.ctc].filter(Boolean).join(" · ") || "—"}
                      </p>
                      <p className="text-xs text-muted-foreground max-w-xs mt-0.5">{h.description}</p>
                    </TableCell>
                    <TableCell>{h.want_consultant ? <span className="text-xs font-semibold text-primary">Yes</span> : <span className="text-xs text-muted-foreground">No</span>}</TableCell>
                    <TableCell>
                      <Button size="sm" variant={h.published ? "default" : "outline"} className="rounded-full h-7 text-xs" onClick={() => togglePublish(h.id, !h.published)} data-testid={`publish-${h.id}`}>
                        {h.published ? "Published" : "Publish"}
                      </Button>
                    </TableCell>
                    <TableCell><StatusBadge status={h.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Recruiters approval */}
        <TabsContent value="recruiters" className="mt-5">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recruiters.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No recruiter access requests yet.</TableCell></TableRow>
                ) : recruiters.map((r) => (
                  <TableRow key={r.id} data-testid={`admin-recruiter-${r.id}`}>
                    <TableCell className="text-sm font-medium">{r.name}<p className="text-xs text-muted-foreground font-normal">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p></TableCell>
                    <TableCell className="text-sm">{r.company_name}{r.company_website ? <p className="text-xs text-muted-foreground"><a href={r.company_website} target="_blank" rel="noreferrer" className="hover:underline">{r.company_website}</a></p> : null}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        {r.status !== "approved" && (
                          <Button size="sm" className="rounded-full h-7 text-xs" onClick={() => setRecruiterStatus(r.id, "approved")} data-testid={`approve-recruiter-${r.id}`}>
                            <Check className="h-3.5 w-3.5 mr-1" /> Approve
                          </Button>
                        )}
                        {r.status !== "rejected" && (
                          <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => setRecruiterStatus(r.id, "rejected")} data-testid={`reject-recruiter-${r.id}`}>
                            <X className="h-3.5 w-3.5 mr-1" /> Reject
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Consulting requests */}
        <TabsContent value="consulting" className="mt-5">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consulting.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No consulting requests yet.</TableCell></TableRow>
                ) : consulting.map((c) => (
                  <TableRow key={c.id} data-testid={`admin-consulting-${c.id}`}>
                    <TableCell className="font-medium">{c.company_name}</TableCell>
                    <TableCell className="text-xs">{c.contact_name}<br />{c.contact_email}<br />{c.contact_phone || ""}</TableCell>
                    <TableCell className="text-xs max-w-md text-muted-foreground">{c.message || "—"}</TableCell>
                    <TableCell className="text-xs">{c.source === "hiring_form" ? "Hiring form" : "Consultant form"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Experts */}
        <TabsContent value="experts" className="mt-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {experts.map((e) => (
              <Card key={e.id} className="p-5" data-testid={`admin-expert-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{e.name}</p>
                    <p className="text-xs text-muted-foreground">{e.current_designation}</p>
                  </div>
                  <StatusBadge status={e.status || "active"} />
                </div>
                <p className="text-xs mt-3 flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-primary" />
                  {e.phone ? <span className="font-medium">{e.phone}</span> : <span className="text-muted-foreground italic">No phone on file</span>}
                </p>
                <p className="text-xs mt-1.5 flex items-start gap-1.5 text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                  <span>{(e.available_days || []).join(", ") || "No days set"} · {(e.available_slots || []).length} slots</span>
                </p>
                <ManageExpertDialog expert={e} onSaved={load} />
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="mt-5">
          <ReminderSettings />
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-muted-foreground">Reminders send automatically at your chosen times before each confirmed call.</p>
            <Button size="sm" variant="outline" className="rounded-full" onClick={runReminders} data-testid="run-reminders-btn">
              <Bell className="h-3.5 w-3.5 mr-1.5" /> Run reminders now
            </Button>
          </div>
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>To</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No notifications yet.</TableCell></TableRow>
                ) : notifications.map((n) => (
                  <TableRow key={n.id} data-testid={`admin-notif-${n.id}`}>
                    <TableCell className="text-sm">{n.to || <span className="text-muted-foreground italic">no phone</span>}</TableCell>
                    <TableCell className="text-xs capitalize">{n.recipient_type}</TableCell>
                    <TableCell className="text-xs max-w-md whitespace-pre-wrap text-muted-foreground">{n.body}</TableCell>
                    <TableCell><StatusBadge status={n.status} /> {n.provider === "mock" && <span className="text-[10px] text-muted-foreground ml-1">(mock)</span>}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" /> WhatsApp sending is currently <span className="font-semibold">MOCKED</span>. Add Twilio credentials in backend/.env to send real messages.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const REMINDER_PRESETS = [
  { label: "1 day before", minutes: 1440 },
  { label: "12 hours before", minutes: 720 },
  { label: "3 hours before", minutes: 180 },
  { label: "1 hour before", minutes: 60 },
  { label: "30 min before", minutes: 30 },
];

const ReminderSettings = () => {
  const [offsets, setOffsets] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setOffsets(r.data.reminder_offsets_minutes || [])).catch(() => {});
  }, []);

  const toggle = (m) => setOffsets((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", { reminder_offsets_minutes: offsets });
      setOffsets(res.data.reminder_offsets_minutes || []);
      toast.success("Reminder timing saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 mb-4" data-testid="reminder-settings">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Bell className="h-4 w-4 text-primary" /> Reminder timing</h3>
          <p className="text-xs text-muted-foreground">Choose how far ahead both parties get a WhatsApp reminder (IST).</p>
        </div>
        <Button size="sm" className="rounded-full" onClick={save} disabled={saving} data-testid="save-reminder-settings">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {REMINDER_PRESETS.map((p) => (
          <button key={p.minutes} type="button" onClick={() => toggle(p.minutes)} data-testid={`reminder-offset-${p.minutes}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${offsets.includes(p.minutes) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
            {p.label}
          </button>
        ))}
      </div>
    </Card>
  );
};

const ManageExpertDialog = ({ expert, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(expert.phone || "");
  const [email, setEmail] = useState(expert.email || "");
  const [availability, setAvailability] = useState(expert.availability || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/experts/${expert.id}`, { phone, email, availability });
      toast.success("Expert details updated");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full mt-4 w-full h-8" data-testid={`manage-expert-${expert.id}`}>
          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Phone & availability
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`manage-dialog-${expert.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{expert.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>WhatsApp number (E.164) — private</Label>
            <Input placeholder="+919876543210" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid={`phone-input-${expert.id}`} />
            <p className="text-xs text-muted-foreground">Hidden from the public. Used to notify the expert of bookings & reminders.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Login email (links Google account to this expert)</Label>
            <Input placeholder="expert@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`email-input-${expert.id}`} />
            <p className="text-xs text-muted-foreground">When this person signs in with Google using this email, they get their Expert Portal.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Availability (per day)</Label>
            <AvailabilityGrid value={availability} onChange={setAvailability} testidPrefix={`admin-avail-${expert.id}`} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-full w-full" data-testid={`save-manage-${expert.id}`}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReferDialog = ({ candidate, onDone }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!email.trim()) { toast.error("Enter the recruiter's email"); return; }
    setSending(true);
    try {
      await api.post(`/admin/candidates/${candidate.id}/refer`, { recruiter_email: email, message });
      toast.success("Candidate emailed to recruiter");
      setOpen(false); setEmail(""); setMessage("");
      onDone();
    } catch (e) {
      toast.error("Could not send referral");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full h-8" data-testid={`refer-${candidate.id}`}>
          <Send className="h-3.5 w-3.5 mr-1" /> Refer
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`refer-dialog-${candidate.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">Refer {candidate.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs bg-secondary rounded-lg p-3">
            <p><span className="font-semibold">Role:</span> {candidate.desired_profile}</p>
            <p><span className="font-semibold">Email:</span> {candidate.email} · {candidate.phone || "—"}</p>
            <p><span className="font-semibold">Resume:</span> {candidate.resume_filename || (candidate.resume_url ? "attached" : "none")}</p>
          </div>
          <div className="space-y-1.5">
            <Label>Recruiter / company email</Label>
            <Input placeholder="recruiter@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`refer-email-${candidate.id}`} />
          </div>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Textarea rows={3} placeholder="Why this candidate is a great fit…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid={`refer-message-${candidate.id}`} />
          </div>
          <p className="text-[11px] text-muted-foreground">Email sending is MOCKED until a provider (Resend) is configured — the message is logged in Notifications.</p>
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={sending} className="rounded-full w-full font-semibold" data-testid={`refer-send-${candidate.id}`}>
            {sending ? "Sending…" : "Send referral email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Admin;
